import createQueryClient from "openapi-react-query";
import type { Client } from "openapi-fetch";
import type { paths } from "./generated";

export interface SpringPagedResponse<T> {
  content: T[];
  number: number;
  totalPages: number;
  size: number;
  totalElements: number;
}

export type QueryApi = ReturnType<typeof createQueryClient<paths, "*/*">>;

export function createQueryApi(
  fetchClient: Client<paths, "*/*">,
): QueryApi {
  return createQueryClient<paths, "*/*">(fetchClient);
}

export function createPaginatedInfiniteQuery(queryApi: QueryApi) {
  return function usePaginatedInfiniteQuery<
    TPagedResponse extends SpringPagedResponse<unknown>,
    TItem = TPagedResponse["content"][number],
  >(
    path: string,
    queryParams: Record<string, unknown>,
    options?: Record<string, unknown>,
    method: "get" | "post" | "put" | "delete" | "patch" = "get",
  ) {
    // openapi-react-query does not export typed useInfiniteQuery; cast required
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query = (queryApi as any).useInfiniteQuery(
      method,
      path,
      { params: { query: queryParams } },
      {
        pageParamName: "page",
        initialPageParam: 0,
        getNextPageParam: (lastPage: TPagedResponse) => {
          return lastPage.number < lastPage.totalPages - 1
            ? lastPage.number + 1
            : undefined;
        },
        ...options,
      },
    );

    const items: TItem[] =
      query.data?.pages.flatMap((p: TPagedResponse) => p.content) ?? [];

    return { ...query, items };
  };
}
